import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { SecurityEventEntity } from 'security-app/hooks/api/securityEventsAPI.types';
import ExpandedEventDetails from 'security-app/components/SecurityEvents/Alerts/ExpandedEventDetails';
import type { DefaultLayout } from 'components/common/EntityDataTable/types';
import type { Event } from 'components/events/events/types';

import BulkActions from '../BulkActions';

const useTableElements = ({ defaultLayout }: { defaultLayout: DefaultLayout }) => {
  const renderExpandedRow = useCallback(
    (event: SecurityEventEntity) => <ExpandedEventDetails securityEvent={event} defaultLayout={defaultLayout} />,
    [defaultLayout],
  );

  const expandedSections = useMemo(
    () => ({
      restFieldsExpandedSection: {
        title: 'Details',
        content: renderExpandedRow,
      },
    }),
    [renderExpandedRow],
  );

  const [selectedEntitiesData, setSelectedEntitiesData] = useState<{ [eventId: string]: Event }>({});
  const bulkSelection = useMemo(
    () => ({
      onChangeSelection: (selectedItemsIds: Array<string>, list: Array<Event>) => {
        setSelectedEntitiesData((cur) => {
          const uniqueSelectedIds = [...new Set(selectedItemsIds)];
          const eventsMap = Object.fromEntries(list.map((event) => [event.id, event]));

          return Object.fromEntries(
            uniqueSelectedIds.map((selectedItemId) => [
              selectedItemId,
              cur[selectedItemId] ?? eventsMap[selectedItemId],
            ]),
          );
        });
      },
      actions: <BulkActions selectedEntitiesData={selectedEntitiesData} />,
    }),
    [selectedEntitiesData],
  );

  return {
    expandedSections,
    bulkActions: <BulkActions selectedEntitiesData={selectedEntitiesData} />,
    bulkSelection,
  };
};

export default useTableElements;
